import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CalendarDays, Loader2 } from "lucide-react";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import BusinessHeader from "../business/BusinessHeader";
import BusinessFooter from "../business/BusinessFooter";
import { Seo, breadcrumbJsonLd, ORG_ID, SITE_URL } from "../Seo";

// Artikler fra BabyLoveGrowth, gemt i vores egen database. På build-tid lægger prerender
// de publicerede artikler i globalThis.__VOTRA_DB_ARTICLES__, så HTML'en er komplet uden JS.
const API = `${process.env.REACT_APP_BACKEND_URL}/api/viden/articles`;
const preloaded = (key) => (typeof globalThis !== "undefined" && globalThis.__VOTRA_DB_ARTICLES__?.[key]) || null;

const THEMES = {
  danmark: {
    base: "/viden", label: "Viden", org: "Votra Danmark", orgId: ORG_ID, brand: "education", Head: Header, Foot: Footer,
    page: "#F8FAFC", hero: "#0B1F3A", accent: "#1D6FE0",
    links: [["/kurser", "Se alle kurser og uddannelser"], ["/kalender", "Find næste holdstart"], ["/kontakt", "Kontakt en studievejleder"]],
  },
  business: {
    base: "/business/viden", label: "Viden · Votra Business", org: "Votra Business", orgId: `${SITE_URL}/business#organization`, brand: "business",
    Head: BusinessHeader, Foot: BusinessFooter, page: "#F8F7F4", hero: "#0A192F", accent: "#C8A14A",
    links: [["/business/selskabsstiftelse", "Opret dit ApS med Votra Business"], ["/business", "Bogføring, løn og drift"], ["/business/viden", "Mere viden for virksomheder"]],
  },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("da-DK", { day: "numeric", month: "long", year: "numeric" }) : "");

const articleLd = (a, t, url) => ({
  "@context": "https://schema.org", "@type": "Article", headline: a.title, description: a.meta_description,
  image: a.hero_image_url ? [a.hero_image_url] : undefined, datePublished: a.published_at, dateModified: a.updated_at || a.source_updated_at || a.published_at,
  inLanguage: a.language || "da", mainEntityOfPage: url,
  author: { "@type": "Organization", name: t.org, "@id": t.orgId }, publisher: { "@type": "Organization", name: t.org, "@id": t.orgId, url: `${SITE_URL}${t.base === "/viden" ? "" : "/business"}` },
});

const asList = (x) => (Array.isArray(x) ? x : x ? [x] : []);

const RelatedLinks = ({ t, related }) => (
  <aside className="mt-14 rounded-2xl border border-slate-200 bg-white p-6 md:p-8" data-testid="db-article-related">
    <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">Læs også</h2>
    <ul className="space-y-2">
      {related.map((r) => <li key={r.slug}><Link to={`${t.base}/${r.slug}`} className="inline-flex items-center gap-2 hover:underline" style={{ color: t.accent }}><ArrowRight className="h-4 w-4" />{r.title}</Link></li>)}
      {t.links.map(([to, label]) => <li key={to}><Link to={to} className="inline-flex items-center gap-2 hover:underline" style={{ color: t.accent }}><ArrowRight className="h-4 w-4" />{label}</Link></li>)}
    </ul>
  </aside>
);

const Body = ({ a, t, related }) => (
  <main id="indhold" className="flex-1">
    <section className="relative overflow-hidden" style={{ backgroundColor: t.hero }}>
      {a.hero_image_url && <div className="absolute inset-0 opacity-25" style={{ backgroundImage: `url(${a.hero_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />}
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ backgroundColor: t.accent }} />
      <div className="relative max-w-[1000px] mx-auto px-4 sm:px-6 py-16 md:py-24">
        <Link to={t.base} className="inline-block text-xs font-semibold uppercase tracking-[0.25em] mb-5 hover:underline" style={{ color: t.accent }} data-testid="db-article-back">{t.label}</Link>
        <h1 className="font-display text-white font-medium leading-[1.12] text-3xl md:text-5xl max-w-3xl mb-5" data-testid="db-article-title">{a.title}</h1>
        <p className="text-slate-300 text-lg max-w-2xl mb-6">{a.meta_description}</p>
        <div className="flex flex-wrap gap-5 text-sm text-slate-400"><span>Af {t.org}</span><span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4" />{fmt(a.published_at)}</span></div>
      </div>
    </section>
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12 md:py-16">
      <div className="viden-prose" style={{ "--viden-accent": t.accent }} data-testid="db-article-body" dangerouslySetInnerHTML={{ __html: a.content_html }} />
      <RelatedLinks t={t} related={related} />
    </article>
  </main>
);

export const DbArticle = ({ company, slug, notFound }) => {
  const t = THEMES[company];
  const key = `${company}/${slug}`;
  const [a, setA] = useState(() => preloaded(key));
  const [state, setState] = useState(a ? "ok" : "loading");
  const [related, setRelated] = useState([]);
  // Tjekker altid mod API'et, så en afpubliceret artikel forsvinder, selv om den ligger i den statiske HTML
  useEffect(() => {
    if (!preloaded(key)) setState("loading");
    axios.get(`${API}/${company}/${slug}`)
      .then((r) => { if (r.data.redirect_to) { setState("moved:" + r.data.redirect_to); return; } setA(r.data); setState("ok"); })
      .catch(() => setState("missing"));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    axios.get(API, { params: { company } }).then((r) => setRelated(r.data.filter((x) => x.slug !== slug).slice(0, 3))).catch(() => {});
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (state === "missing") return notFound;
  if (state.startsWith("moved:")) return <Navigate to={state.slice(6)} replace />;
  if (state === "loading") return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const path = `${t.base}/${a.slug}`;
  const url = `${SITE_URL}${path}`;
  const { Head, Foot } = t;
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: t.page }} data-testid="db-article">
      <Seo title={`${a.title} | ${t.org}`} description={a.meta_description} path={path} image={a.hero_image_url || undefined} brand={t.brand}
        jsonLd={[articleLd(a, t, url), breadcrumbJsonLd([{ name: t.org, path: t.base === "/viden" ? "/" : "/business" }, { name: "Viden", path: t.base }, { name: a.title, path }]),
          ...asList(a.json_ld), ...asList(a.faq_json_ld)]} />
      <Head />
      <Body a={a} t={t} related={related} />
      <Foot />
    </div>
  );
};
